import type { Money } from "@workspace/accounting/types";

import type { TabDetail } from "../types";

/**
 * HA-554 Part 2 — why Pay Tab can't take a payment now. "offline": refused offline, like every order write (an offline
 * Pay Tab cash payment needs HA-164's queue). "unsent": an order on the tab has lines the server doesn't have yet.
 */
export type PayTabBlock =
	"offline" | "unsent" | "closed" | "nothing-owed" | "card-in-flight";

export const payTabBlockMessage: Record<PayTabBlock, string> = {
	offline: "Pay Tab needs a connection. Reconnect, then take the payment.",
	unsent:
		"An order on this tab has items that haven't reached the server yet. Send them first.",
	closed: "This tab is closed.",
	"nothing-owed": "Nothing is outstanding on this tab.",
	"card-in-flight":
		"A card payment is in progress on this tab. Finish or cancel it first.",
};

export function payTabBlock(
	detail: TabDetail,
	state: { online: boolean; hasUnsentLines: (orderId: string) => boolean },
): PayTabBlock | null {
	if (detail.tab.status !== "Open") return "closed";
	if (!state.online) return "offline";
	if (detail.tab.cardPaymentsInFlight.length > 0) return "card-in-flight";
	if (detail.orders.some((o) => state.hasUnsentLines(o.id))) return "unsent";
	if (detail.figures.outstanding.value <= 0) return "nothing-owed";
	return null;
}

export interface TabPaymentLine {
	tabPaymentId: string;
	method: "cash" | "card";
	amount: Money;
	shares: { round: number; amount: Money }[];
}

/** Each recorded payment, with its share of each round (the round is the order's place on the tab, oldest first). */
export function tabPaymentLines(detail: TabDetail): TabPaymentLine[] {
	const round = (orderId: string) =>
		detail.orders.findIndex((o) => o.id === orderId) + 1;

	return detail.tab.payments.map((p) => ({
		tabPaymentId: p.tabPaymentId,
		method: p.method,
		amount: p.amount,
		shares: p.allocation.map((s) => ({
			round: round(s.orderId),
			amount: s.amount,
		})),
	}));
}

/**
 * The amount typed in, in minor units (two decimal places): more than nothing and at most what the tab owes, or null.
 * The server judges it again.
 */
export function parseTabAmount(text: string, outstanding: Money): Money | null {
	const match = /^\s*(\d+)(?:\.(\d{1,2}))?\s*$/.exec(text);
	if (!match) return null;

	const minor =
		Number(match[1]) * 100 + Number((match[2] ?? "").padEnd(2, "0"));
	if (minor <= 0 || minor > outstanding.value) return null;

	return { ...outstanding, value: minor };
}

/** A Pay Tab payment that didn't go through: what to tell the operator, and whether asking again could help. */
export interface PayTabFailure {
	title: string;
	description: string;
	/**
	 * Nothing was said about the payment (no answer, a 5xx), so it may have landed: asked again as the same payment, it is
	 * answered as the first was. Otherwise the server's answer is final and the next attempt is a new payment.
	 */
	transient: boolean;
}

const PAY_TAB_REFUSALS: Record<string, Omit<PayTabFailure, "transient">> = {
	TAB_CLOSED: {
		title: "This tab is closed",
		description: "Nothing more can be paid on it.",
	},
	TAB_PAYMENT_EXCEEDS_OUTSTANDING: {
		title: "That's more than the tab owes",
		description: "The tab has been read again. Check the amount and try again.",
	},
	TAB_PAYMENT_TIP_OUT_OF_RANGE: {
		title: "That tip can't be taken",
		description: "A tip is more than nothing and at most the payment.",
	},
	TIPS_DISABLED: {
		title: "This venue doesn't take tips",
		description: "Take the payment without a tip.",
	},
	TAB_PAYMENT_IN_PROGRESS: {
		title: "A card payment is in progress on this tab",
		description: "Finish or cancel it before taking another payment.",
	},
	ORDER_HELD_BY_TAB_PAYMENT: {
		title: "A card payment is in progress on this tab",
		description: "Finish or cancel it before taking another payment.",
	},
	CASH_PAYMENT_ALREADY_RECORDED: {
		title: "That payment was already recorded",
		description:
			"The tab has been read again. Check what it owes before taking anything more.",
	},
};

/**
 * review2 on #439 — the till's own words for a refused Pay Tab payment, keyed on the server's code, never its detail; and
 * Retry only for a failure that says nothing about the payment.
 */
export function payTabFailure(error: unknown): PayTabFailure {
	const response = (error as { response?: { status?: number } } | null)
		?.response;
	const problem = (error as { problem?: { code?: string } } | null)?.problem;
	const status = response?.status;

	if (status === undefined || status >= 500)
		return {
			title: "The tab payment couldn't be recorded",
			description:
				"The till didn't get an answer. Try again: the same payment is sent, so it can't be taken twice.",
			transient: true,
		};

	const refusal = problem?.code ? PAY_TAB_REFUSALS[problem.code] : undefined;
	return {
		...(refusal ?? {
			title: "The tab payment wasn't taken",
			description: "The tab has been read again. Check it and try again.",
		}),
		transient: false,
	};
}
